import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { computed } from 'mobx';
import { withTheme } from 'styled-components';
import DownloadControls from './Document/DownloadControls';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import StarRating from './StarRating';
import { ShareclassColumn } from './ShareclassRows';
import Icon from './Icon';
import Translate from './Translate';
import DynamicLink from './DynamicLink';
import {
    AccessibilityStore,
    DataStore,
    EntityGroupStore,
    EntityStore,
    HistoryStore,
    SuperscriptScope,
} from '../stores';
import InlineDisclaimers from './InlineDisclaimers';
import DocumentMenu from './Document/DocumentMenu';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../models/configuration/explorer/DownloadControls';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { Group } from '../models/configuration/explorer/Group';
import { DataLocator } from '../models/configuration/DataLocator';
import { DynamicLinkConfig } from '../models/configuration/components/DynamicLinkConfig';
import { Theme } from '../models/Theme';
import { ColumnDocumentsGrid } from '../models/configuration/documents/ColumnDocumentsGrid';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

interface ShareclassColumnValueProps {
    theme?: Theme;
    /** Inject stores */
    dataStore?: DataStore;
    accessibilityStore?: AccessibilityStore;
    historyStore?: HistoryStore;
    entityStore?: EntityStore;
    entityGroupStore?: EntityGroupStore;
    /** */
    columnIndex: number;
    column: ColumnConfig;
    rowAccessibleContext: string;
}

// These types get added along the way, we need to work out where they are added and create interfaces
interface WorkingColumnConfig extends ColumnConfig {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
}

interface WorkingGroupConfig extends Group {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
}
@inject('dataStore', 'historyStore', 'entityStore', 'entityGroupStore', 'accessibilityStore')
@observer
class ShareclassColumnValue extends Component<ShareclassColumnValueProps> {
    @computed
    get selectedGroupIndex() {
        const { dataStore, column, columnIndex } = this.props;
        let index = dataStore.columnStore.selectedGroupMap.get(columnIndex);
        if (column.groups) {
            if (index >= column.groups.length) {
                const selectedConfigGroupIndex = column.groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }
        return index;
    }

    @computed
    get column() {
        return this.props.column.groups
            ? (this.props.column.groups[
                this.selectedGroupIndex
            ] as WorkingGroupConfig)
            : (this.props.column as WorkingColumnConfig);
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        const { dataStore } = this.props;
        return dataStore?.config.getDownloadControlActionsOrDefault(
            this.props.column?.actions
        );
    }

    getGroupOrDropdownColumnContent = column => {
        const { theme, entityStore } = this.props;
        const shareclassColumnGroupedTheme =
            entityStore.isShareclassDisabled || false
                ? theme.ShareclassColumnGroupedDisabled
                : theme.ShareclassColumnGrouped;

        return (
            column.values &&
            column.values.map((childColumn, cIndex) => {
                return (
                    <ShareclassColumn
                        key={`${column.type}-${childColumn.value}-${cIndex}`}
                        paddingX="sm"
                        paddingY="0"
                        textAlign={childColumn.textAlign || 'center'}
                        width={childColumn.width}
                        whiteSpace={childColumn.wrapText ? 'normal' : 'nowrap'}
                        {...shareclassColumnGroupedTheme}
                    >
                        {this.getColumnContent(childColumn)}
                    </ShareclassColumn>
                );
            })
        );
    };

    getDownloadColumnContent = (column: WorkingColumnConfig) => {
        const { dataStore, entityStore, rowAccessibleContext } = this.props;
        const {
            downloadCount = 0,
            shareClassDownloads,
            value: shareClass,
        } = entityStore;
        return (
            <DownloadControls
                count={downloadCount}
                display="inline-flex"
                disabled={downloadCount === 0}
                componentThemeKey="ShareclassRow"
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    dataStore.downloadDocuments(
                        shareClassDownloads,
                        shareClass
                    );
                }}
                href={dataStore.getDocumentLink(
                    shareClassDownloads,
                    shareClass
                )}
                onPreviewClick={(e, elementOptions: PreviewActionOptions) => {
                    e.stopPropagation();
                    dataStore.previewDocuments(
                        shareClassDownloads,
                        shareClass,
                        elementOptions
                    );
                }}
                showLabel={false}
                label={dataStore.getFileName(shareClassDownloads)}
                fileName={dataStore.getFileName(
                    shareClassDownloads,
                    shareClass
                )}
                metricContext={`shareclassColumn-${camelCase(
                    column.label
                )}-${camelCase(shareClass.clientCode)}`}
                document={shareClassDownloads}
                documentDisplayName={camelCase(shareClass.clientCode)}
                actions={this.downloadControlActions}
                disableExtendedInfo={column.disableExtendedInfo}
                describedBy={rowAccessibleContext}
            />
        );
    };

    @computed
    get watchlistColumnContent() {
        const { entityStore, rowAccessibleContext } = this.props;
        return (
            <WatchListToggle
                onClick={e => {
                    e.stopPropagation();
                    entityStore.toggleWatchList();
                }}
                active={entityStore.inWatchList}
                describedby={rowAccessibleContext}
                metricContext={camelCase(entityStore.value.clientCode)}
            />
        );
    }

    getColumnDocumentsGridConfig(column): ColumnDocumentsGrid | undefined {
        const { columnDocumentsGrid } = column;
        // If the column does not have the documents grid config configured,
        // then return undefined, the getDocumentColumnContent will handle this as normal
        if (isNullOrEmpty(columnDocumentsGrid)) {
            return undefined;
        }
        // If the column config has the documents grid configuration then return as is
        // to override the module level configuration
        if (columnDocumentsGrid.documentsGrid) {
            return column.columnDocumentsGrid;
        }
        // Else add the documents grid config that is defined on the module level
        const { dataStore } = this.props;
        if (dataStore) {
            const { documentsGrid } = dataStore.config.selectedModuleConfig;
            if (documentsGrid) {
                return {
                    ...columnDocumentsGrid,
                    documentsGrid,
                };
            }
        }
    }

    getDocumentColumnContent = column => {
        const { theme, dataStore, entityStore, entityGroupStore, rowAccessibleContext } = this.props;
        const documents = column.value;
        const isDocumentGrid = Boolean(
            this.getColumnDocumentsGridConfig(column)
        );
        // We will fetch the documents if the dropdown displays the document grid
        if (
            Array.isArray(column.value) &&
            (documents.length > 0 || isDocumentGrid)
        ) {
            // The document menu does not have a prop called documentSelected
            return (
                <DocumentMenu
                    documents={documents}
                    selected={entityStore.documentSelected}
                    onChange={entityGroupStore.toggleDownload}
                    showCount={isDocumentGrid}
                    showCheckbox={isDocumentGrid}
                    showSingleDocument={column.showSingleDocument}
                    showSingleDocumentLabel={column.showSingleDocumentLabel}
                    theme={{
                        ...theme,
                        ...{
                            DownloadButtonIconNoLabel: {
                                margin: '0 auto',
                            },
                            DownloadControls: column.showSingleDocument &&
                                documents.length === 1 && {
                                justifyContent: 'center',
                            },
                        },
                    }}
                    metricContext={`shareclassColumn-${camelCase(
                        entityStore.value.clientCode
                    )}`}
                    columnDocumentsGrid={this.getColumnDocumentsGridConfig(
                        column
                    )}
                    describedBy={rowAccessibleContext}
                >
                    <Icon
                        fileName="DocumentIcon"
                        height="1.4em"
                        width="1.4em"
                        aria-hidden="true"
                        {...theme.ShareclassRowDocumentIcon}
                    />
                </DocumentMenu>
            );
        }
        return (
            <Translate>
                {column.noDataPlaceholder || dataStore.config.noDataPlaceholder}
            </Translate>
        );
    };
    getPricesColumnContent = column => {
        const { historyStore, entityStore, rowAccessibleContext } = this.props;
        if (column.data.performanceCheck && !entityStore.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <DownloadControls
                    display="inline-flex"
                    onClick={e => {
                        e.stopPropagation();
                        historyStore.getPriceHistory(
                            column,
                            entityStore.value.clientCode
                        );
                    }}
                    count={1}
                    showCount={false}
                    hidePreview={true}
                    // Due to historic prices building up filename on the response, we can not accurately set this. Default to historic_prices.csv
                    fileName={'historic_prices.csv'}
                    metricContext={`shareclassColumn-${camelCase(
                        column.label
                    )}-${camelCase(entityStore.value.clientCode)}`}
                    disableExtendedInfo={column.disableExtendedInfo}
                    actions={this.downloadControlActions}
                    describedBy={rowAccessibleContext}
                />
            );
        }
    };
    getStarsColumnContent = column => {
        const { theme, entityStore } = this.props;
        if (column.data.performanceCheck && !entityStore.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <StarRating
                    rating={column.data.value}
                    noDataPlaceholder={column.noDataPlaceholder}
                    showEmptyStars={column.type === 'starsOutOf'}
                    display="block"
                    theme={{
                        ...theme,
                        ...theme.StarRatingColumnWrapper,
                        StarIcon: {
                            ...starIconStyleProps,
                            ...theme.StarIconColumn,
                        },
                        StarOutlineIcon: {
                            ...starIconStyleProps,
                            ...theme.StarOutlineIconColumn,
                        },
                    }}
                />
            );
        }
    };
    getSimpleColumnContent = (column, data, key: any = undefined) => {
        const { dataStore } = this.props;
        const separator = column.multiple && data.separator;
        return (
            <Fragment key={key}>
                {data.showChange ? (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={data.showChange}
                        value={data.value}
                        separator={separator}
                    />
                ) : (
                    <Fragment>
                        <Translate>{data.value}</Translate>
                        {separator || null}
                    </Fragment>
                )}
            </Fragment>
        );
    };
    getColumnReplacementValue(column): string {
        const dataRecords = isArrayLike(column.data)
            ? column.data
            : [column.data];
        let value = '';
        dataRecords.forEach(data => {
            value += `${data.value}${column.multiple && data.separator ? data.separator : ''
                }`;
        });
        return value;
    }
    onClick = (link: string) => {
        const { dataStore } = this.props;
        dataStore.logDataLayer(
            'entity_data_column',
            {
                url: link,
                [this.column.label]: this.getColumnReplacementValue(
                    this.column
                ),
            },
            'click'
        );
    };
    getColumnContent = (column: WorkingColumnConfig | WorkingGroupConfig) => {
        const { theme, dataStore, entityStore, accessibilityStore } = this.props;

        let columnContent;
        // Determine which column types can be wrapper with an anchor
        let hasLink = false;
        if (['group', 'dropdown'].includes(column.type)) {
            hasLink = true;
            columnContent = this.getGroupOrDropdownColumnContent(column);
        } else if (column.type === 'download') {
            columnContent = this.getDownloadColumnContent(column);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            columnContent = this.watchlistColumnContent;
        } else if (column.type === 'document') {
            columnContent = this.getDocumentColumnContent(column);
        } else if (column.type === 'prices') {
            columnContent = this.getPricesColumnContent(column);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            columnContent = this.getStarsColumnContent(column);
        } else if (column.multiple) {
            hasLink = true;
            columnContent = (
                <span id={(column as any).elementId}>
                    {(column.data as Array<DataLocator>).map((data, i) =>
                        this.getSimpleColumnContent(column, data, i)
                    )}
                </span>
            );
        } else {
            hasLink = true;
            columnContent = (
                <span id={(column as any).elementId}>
                    {this.getSimpleColumnContent(column, column.data)}
                </span>
            );
        }

        const metricContext = `shareclassColumn-${camelCase(
            column.label
        )}-${camelCase(entityStore?.value?.clientCode || '')}`;
        if (hasLink && column.url) {
            const columnReplacementValue = this.getColumnReplacementValue(
                column
            );

            const dynamicLinkConfig: DynamicLinkConfig = {
                key: column.key,
                url: column.url,
            };

            return (
                <InlineDisclaimers
                    accessibilityContext={{
                        key: `More information about the {label}`,
                        placeholders: {
                            // Using the link accesibility value as the placeholder so that it lines up with the aria label for the dynamic link
                            label: accessibilityStore.linkAccessibilityValue(
                                'shareClassRowLinks',
                                {
                                    label: column.label,
                                    value: columnReplacementValue,
                                }
                            ),
                        }
                    }}
                    conditionalDisclaimers={column.cellDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        metricContext,
                        source: 'shareclassColumn',
                        sourceContext: columnReplacementValue,
                    }}
                >
                    <DynamicLink
                        config={dynamicLinkConfig}
                        dataStore={dataStore}
                        metricContext={metricContext}
                        ariaLabelKey={'shareClassRowLinks'}
                        ariaLabelReplacements={{
                            label: column.label,
                            value: columnReplacementValue,
                        }}
                        onClick={this.onClick}
                        {...theme.ShareclassColumnLink}
                    >
                        {columnContent}
                    </DynamicLink>
                </InlineDisclaimers>
            );
        }

        return (
            <InlineDisclaimers
                accessibilityContext={{
                    key: `More information about the {label}`,
                    placeholders: {
                        label: columnContent
                    }
                }}
                conditionalDisclaimers={column.cellDisclaimers}
                superscriptScope={SuperscriptScope.module}
                analyticsContext={{
                    metricContext,
                    source: 'shareclassColumn',
                    sourceContext: columnContent,
                }}
            >
                {columnContent}
            </InlineDisclaimers>
        );
    };
    render() {
        return this.getColumnContent(this.column);
    }
}

export default withTheme(ShareclassColumnValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassColumnValue.tsx